import { sessionStore, setSelectedGift } from "../../stores/session";
import { get } from "svelte/store";
import type { OrderEvent } from "../../../types/orderEvent";
import PATHS from "../../constants/routePaths";
import { navigateToPath } from "../../routing";
import { getWorkflowInfo } from "../../apiLoader/campaign";
import { getWS } from "../../ws/progressWSLoader";
import { type ProgressWSInterface } from "../../ws/progressWSInterface";
import type { LatLngLiteral } from "../../../types/map";
import { mapDeliveryPinSrc, mapPinSrc } from "../../constants/map";

export type CustomerDeliveryData = {
  status: string;
  pickup?: {
    latitude: number;
    longitude: number;
  };
  destination: {
    latitude: number;
    longitude: number;
  };
};

export type OnStatusUpdateInput = {
  event: OrderEvent | undefined;
  data: CustomerDeliveryData;
  onDeliveryProgressUpdate: (event: OrderEvent, progressLocations: { icon: string, coords: LatLngLiteral }[]) => void;
};

export const onStatusUpdate = ({ event, data, onDeliveryProgressUpdate }: OnStatusUpdateInput) => {
  const mergedEvent = Object.assign({}, data, event);

  if(mergedEvent.status === "stock-out") {
    navigateToPath(PATHS.STOCK_OUT);
  } else if(["rate-limited", "error", "cancelled", "returned"].includes(mergedEvent.status)) {
    navigateToPath(PATHS.CANCELLED);
  } else if(mergedEvent.status === "delivered") {
    // This is just to show the final destination marker so that we don't overlapping markers
    delete mergedEvent.pickup;
    mergedEvent.courier = mergedEvent.destination;
  }

  const LocationIconMap: { [key: string]: string } = {
    pickup: mapPinSrc,
    destination: mapPinSrc,
    courier: mapDeliveryPinSrc
  };

  const locations = ["pickup", "destination", "courier"].map((location) => {
    const locationData =
      mergedEvent[location as keyof (CustomerDeliveryData & OrderEvent)] as {
        latitude: number; longitude: number
      } | undefined;
    if(locationData) {
      return {
        icon: LocationIconMap[location],
        coords: {
          lat: locationData.latitude,
          lng: locationData.longitude
        } as LatLngLiteral
      }; 
    }
  });
  onDeliveryProgressUpdate(mergedEvent, locations.filter(Boolean) as { icon: string, coords: LatLngLiteral }[]);
};

export const startDeliveryProgressTracker = async (
  onDeliveryProgressUpdate: (event: OrderEvent, progressLocations: { icon: string, coords: LatLngLiteral }[]) => void
): Promise<ProgressWSInterface> => {
  const session = get(sessionStore);

  const { process } = await getWorkflowInfo(session.internalProcessKey as string);
  const pickupLocation = process?.metadata["pickup.item"]?.pickup_location;
  const customerData = process?.metadata["customer.settings"]?.customer_data;

  const data: CustomerDeliveryData = {
    status: "pending",
    pickup: pickupLocation,
    destination: customerData
  };

  const progressTracker = getWS({
    onStatusUpdate: (status) => {
      onStatusUpdate({ event: status, data, onDeliveryProgressUpdate });
    },
    jwt: session.jwt!,
    internalProcessKey: session.internalProcessKey
  });

  onStatusUpdate({ event: undefined, data, onDeliveryProgressUpdate });

  progressTracker.connect();

  const deliveryItems = process?.metadata["delivery_items"];
  const selectedGift = process?.metadata["pickup.item"] || deliveryItems?.[0];
  setSelectedGift({
    variation: selectedGift?.variation_key,
    name: selectedGift?.name_key,
    image: selectedGift?.image
  });

  return progressTracker;
};

export const stopDeliveryProgressTracker = (progressTracker: ProgressWSInterface | null) => {
  progressTracker?.disconnect();
};
